// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";

import { Accordion, AccordionDetails, AccordionSummary, CardActionArea, Dialog, DialogActions, DialogContent, DialogTitle, Grid, List, ListItem } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

import { useState } from "react";
import MDButton from "../MDButton";
import MDBox from "../MDBox";
import MDTypography from "../MDTypography";
import PercentageBar from "../PercentageBar";

// Images
import phantomNodeImg from "../../assets/images/phantom_node.png";
import spectraFlexImg from "../../assets/images/assetImages/SpectraFlex Adaptive Wear.png";
import morphoGloveImg from "../../assets/images/assetImages/Morpho-Touch Glove.png";
import missionScopeImg from "../../assets/images/assetImages/MissionScope Planner.png";
import swarmImg from "../../assets/images/assetImages/Swarm Intelligence Deployment Suite.png";
import advTactMobileImg from "../../assets/images/assetImages/Advanced Tactical Mobility System.png";
import stingrayImg from "../../assets/images/assetImages/STINGRAY-X — Autonomous Littoral Navigation Beacon (ALNB).png";
import disruptionImg from "../../assets/images/assetImages/Cyber-Physical Disruption Suite (CPDS).png";
import seopImg from "../../assets/images/assetImages/Strategic Effects Orchestration Platform (SEOP).png";
import agsfsImg from "../../assets/images/assetImages/Adaptive Ground Sensing & Fusion System (AGS-FS).png";
import tgtihImg from "../../assets/images/assetImages/Tactical Geospatial & Threat Intelligence Hub (TG-TIH).png";
import iideImg from "../../assets/images/assetImages/Intelligent Information Distribution Engine (IIDE).png";
import sddcmImg from "../../assets/images/assetImages/Secure Device & Data Concealment Module (SDDCM).png";

function AssetCard({ image, title, description, specs, capabilities }) {
  const [showAssetInfo, updateShowAssetInfo] = useState(false);

    const imageDictionary = {
        "Phantom Node": phantomNodeImg,
        "SpectraFlex Adaptive Wear": spectraFlexImg,
        "Morpho-Touch Glove": morphoGloveImg,
        "MissionScope Planner": missionScopeImg,
        "Swarm Intelligence Deployment Suite": swarmImg,
        "Advanced Tactical Mobility System": advTactMobileImg,
        "STINGRAY-X — Autonomous Littoral Navigation Beacon (ALNB)": stingrayImg,
        "Cyber-Physical Disruption Suite (CPDS)": disruptionImg,
        "Strategic Effects Orchestration Platform (SEOP)": seopImg,
        "Adaptive Ground Sensing & Fusion System (AGS-FS)": agsfsImg,
        "Tactical Geospatial & Threat Intelligence Hub (TG-TIH)": tgtihImg,
        "Intelligent Information Distribution Engine (IIDE)": iideImg,
        "Secure Device & Data Concealment Module (SDDCM)": sddcmImg
    }

  return (
    <Card sx={{height: '100%'}}>
        <CardActionArea onClick={() => updateShowAssetInfo(true)}>
                <MDBox position="relative" borderRadius="lg" mt={1} mx={2}>
                    <MDBox
                    component="img"
                    src={imageDictionary[title]}
                    alt={title}
                    borderRadius="lg"
                    shadow="md"
                    width="100%"
                    height="100%"
                    position="relative"
                    zIndex={1}
                    />
                    <MDBox
                    borderRadius="lg"
                    shadow="md"
                    width="100%"
                    height="100%"
                    position="absolute"
                    left={0}
                    top="3%"
                    sx={{
                        backgroundImage: `url(${image})`,
                        transform: "scale(0.94)",
                        filter: "blur(12px)",
                        backgroundSize: "cover",
                    }}
                    />
                </MDBox>
                <MDBox p={3}>
                    <MDTypography display="inline" variant="h3" textTransform="capitalize" fontWeight="bold">
                        {title}
                    </MDTypography>
                </MDBox>
        </CardActionArea>

        <Dialog 
            open={showAssetInfo} 
            onClose={() => updateShowAssetInfo(false)}
            maxWidth="xs"
        >
            <DialogTitle>
                <MDTypography display="inline" variant="h4" textTransform="capitalize" fontWeight="bold">
                    {title}
                </MDTypography>
            </DialogTitle>
            <DialogContent>
                <Grid container>
                    <Grid item lg={12}> 
                        <MDBox
                            component="img"
                            src={imageDictionary[title]}
                            alt={title}
                            borderRadius="lg"
                            shadow="md"
                            width="100%"
                            height="100%"
                        />
                    </Grid>
                    <Grid mt={1} item lg={12}>
                        <MDTypography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                            {description}
                        </MDTypography>
                    </Grid>
                    <Grid item mt={1} lg={12}>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore/>} sx={{backgroundColor: "navy.main"}}>
                                <MDTypography variant="h6">Specs</MDTypography>
                            </AccordionSummary>
                            <AccordionDetails sx={{backgroundColor: "navy.main"}}>
                                <MDBox ml={2} mr={1} display="flex" flexDirection="columns" sx={{backgroundColor: "navy.main"}}>
                                    <List sx={{listStyleType: "disc"}}>
                                        {specs.map((spec, specIndex) => (
                                            <ListItem key={`spec-${specIndex}`} sx={{ display: 'block', mb: 2 }}>
                                                <MDTypography component="div" variant="body2" sx={{color: "text.main"}}>
                                                    <MDBox fontWeight="bold" display="inline" sx={{color: "text.main"}}>
                                                        {spec.header}:
                                                    </MDBox>
                                                </MDTypography>
                                                <MDBox pl={2} mt={1}>
                                                    {Array.isArray(spec.description) ? (
                                                        spec.description.map((descItem, descIndex) => (
                                                            <MDTypography 
                                                                key={`desc-${specIndex}-${descIndex}`}
                                                                component="div" 
                                                                variant="body2"
                                                                sx={{ mb: 0.5 }}
                                                            >
                                                                • {descItem}
                                                            </MDTypography>
                                                        ))
                                                    ) : (
                                                        <MDTypography component="div" variant="body2">
                                                            {spec.description}
                                                        </MDTypography>
                                                    )}
                                                </MDBox>
                                            </ListItem>
                                        ))}
                                    </List>
                                </MDBox>
                            </AccordionDetails>
                        </Accordion>
                        <Accordion sx={{backgroundColor: "navy.main"}}>
                            <AccordionSummary expandIcon={<ExpandMore/>} sx={{backgroundColor: "navy.main"}}>
                                <MDTypography variant="h6">Capabilities</MDTypography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container>
                                    {capabilities.map((item) => (
                                        <Grid item lg={12} mb={1}>
                                            <Accordion>
                                                <AccordionSummary expandIcon={<ExpandMore/>}>
                                                    <Grid container>
                                                        <Grid item lg={12}>
                                                            <MDTypography fontSize={16}>{item.main}</MDTypography>
                                                        </Grid>
                                                        <Grid item xs={12} lg={12}>
                                                            <PercentageBar value={item.value}/>
                                                        </Grid>
                                                    </Grid>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    {item.subset.map((subItem) => (
                                                        <Grid container sx={{width: "90%", margin: "0 auto"}}>
                                                            <Grid item lg={12}>
                                                                <MDTypography fontSize={16}>{subItem.name}</MDTypography>
                                                            </Grid>
                                                            <Grid item xs={12} lg={12}>
                                                                <PercentageBar value={subItem.value} color="#64D5CF"/>
                                                            </Grid>
                                                        </Grid>
                                                    ))}
                                                </AccordionDetails>
                                            </Accordion>
                                        </Grid>
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={() => updateShowAssetInfo(false)} color="stormblue">
                    Close
                </MDButton>
            </DialogActions>
        </Dialog>
    </Card>
  );
}

// Typechecking props for the SimpleBlogCard
AssetCard.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

export default AssetCard;